import { Link, NavLink, Outlet } from 'react-router-dom'
import { useState } from 'react'
import { useAuth } from '../app/providers/AuthProvider'

const navItems = [
  { label: 'Dashboard', path: '/app' },
  { label: 'Projects', path: '/app/projects' },
  { label: 'Deployments', path: '/app/deployments' },
  { label: 'Settings', path: '/app/settings' },
]

const AppShell = () => {
  const { user, logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    <div className="relative min-h-screen bg-surface text-ink">
      <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
      <div className="absolute -top-20 right-8 h-60 w-60 rounded-full bg-glow-cyan" aria-hidden="true" />

      <div className="relative z-10 flex min-h-screen w-full">
        <aside className="glass-panel sticky top-0 hidden h-screen w-64 flex-col rounded-r-3xl p-6 md:flex">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logo.jpeg"
              alt="Flynt logo"
              className="h-10 w-20 bg-surface object-contain logo-zoom"
            />
            <div>
              <div className="text-lg font-semibold">Flynt</div>
              <div className="text-xs text-slate-500">by Inflynx</div>
            </div>
          </Link>

          <nav className="mt-10 flex flex-col gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.label}
                to={item.path}
                end={item.path === '/app'}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? 'brand-button text-white shadow-sm'
                      : 'text-slate-600 hover:bg-white/80'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto rounded-2xl border border-slate-200 bg-white/70 p-4 text-xs text-slate-600">
            <div className="text-sm font-semibold text-ink">Free plan</div>
            <div className="mt-2">3 projects max</div>
            <button className="mt-4 w-full rounded-lg border border-slate-300 py-2 text-xs font-semibold text-ink">
              Upgrade to Pro
            </button>
          </div>
        </aside>

        <main className="flex-1">
          <header className="glass-panel mx-4 mt-4 flex items-center justify-between rounded-2xl px-4 py-4 md:mx-6 md:px-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">Workspace</div>
              <div className="text-lg font-semibold">{user?.email || 'workspace'}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-500 md:flex">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                infra healthy
              </div>
              <button className="brand-button hidden rounded-full px-4 py-2 text-sm font-semibold md:inline-flex">
                New deploy
              </button>
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-sm md:hidden"
              >
                =
              </button>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((open) => !open)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 bg-white text-xs font-semibold text-slate-600"
                >
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </button>
                {menuOpen ? (
                  <div className="glass-panel absolute right-0 mt-2 w-44 rounded-2xl">
                    <Link to="/app/profile" className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">
                      Profile
                    </Link>
                    <Link to="/app" className="block px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">
                      Dashboard
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        logout()
                        setMenuOpen(false)
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-100"
                    >
                      Logout
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          <div className="px-4 py-6 md:px-6 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      {mobileNavOpen ? (
        <div className="fixed inset-0 z-40 bg-black/20 md:hidden">
          <div className="absolute left-0 top-0 h-full w-72 bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src="/logo.jpeg"
                  alt="Flynt logo"
                  className="h-10 w-20 bg-surface object-contain logo-zoom"
                />
                <div>
                  <div className="text-lg font-semibold">Flynt</div>
                  <div className="text-xs text-slate-500">by Inflynx</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="text-sm text-slate-600"
              >
                X
              </button>
            </div>

            <nav className="mt-8 flex flex-col gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.path}
                  end={item.path === '/app'}
                  onClick={() => setMobileNavOpen(false)}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-2 text-sm font-semibold transition ${
                      isActive
                        ? 'brand-button text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-200'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-100 p-4 text-xs text-slate-600">
              <div className="text-sm font-semibold text-ink">Free plan</div>
              <div className="mt-2">3 projects max</div>
              <button className="mt-4 w-full rounded-lg border border-slate-300 py-2 text-xs font-semibold text-ink">
                Upgrade to Pro
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default AppShell
